import { collection, query, where, getDocs, orderBy, Timestamp } from "firebase/firestore";
import { db } from "src/firebase";

export interface TrendDataPoint {
  date: string;
  learners: number;
  coaches: number;
  dual: number;
  total: number;
}

export interface TrendDataFilters {
  timeGranularity: "daily" | "weekly" | "custom";
  metric: "users" | "sessions" | "bookings" | "lounge" | "slots" | "rooms" | "ai-sessions";
  startDate?: Date;
  endDate?: Date;
  role?: "ALL" | "COACH" | "LEARNER" | "DUAL";
  language?: string;
  timeZone?: string;
  sessionState?: "ALL" | "STANDBY" | "REJECTED" | "MISSED" | "ACTIVE" | "COMPLETED" | "CANCELLED";
  sessionType?: "ALL" | "DIRECT" | "BOOKING" | "LOUNGE";
  coachType?: "ALL" | "PRO" | "VOLUNTEER";
  bookingStatus?: "ALL" | "INIT" | "CANCELLED" | "ACCEPTED" | "REJECTED" | "COMPLETED" | "EXPIRED";
  slotStatus?: "ALL" | "AVAILABLE" | "BOOKED" | "EXPIRED" | "SINGLE_REQUEST_PENDING";
  loungeStatus?: "ALL" | "STANDBY" | "CANCELLED" | "ACCEPTED" | "EXPIRED";
  initiatorType?: "ALL" | "LEARNER" | "COACH";
  roomStatus?: "ALL" | "INITIATED" | "EXPIRED" | "ACTIVE" | "SCHEDULED" | "ENDED" | "CANCELLED";
  roomCategory?: "ALL" | "CLASSROOM" | "COMMUNITY";
  aiSessionStatus?: "ALL" | "ACTIVE" | "COMPLETED" | "INACTIVE";
}

export interface TrendDataResponse {
  data: TrendDataPoint[];
  totalPoints: number;
  timeRange: {
    start: string;
    end: string;
  };
}

class TrendDataService {
  private static readonly DATE_FIELD_BY_COLLECTION = {
    users: "createdAt",
    sessions: "startTime",
    bookings: "createdAt",
    lounge_requests: "createdAt",
    slots: "createdAt",
    rooms: "startTime",
    ai_sessions: "startTime",
  } as const;
  private async fetchUsersByTimePeriod(filters: TrendDataFilters): Promise<TrendDataPoint[]> {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    // Calculate date ranges based on granularity
    switch (filters.timeGranularity) {
      case "daily":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29); // Last 30 days
        break;
      case "weekly":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 83); // Last 12 weeks
        break;
      case "custom":
        // Use provided startDate and endDate, or default to last 30 days
        if (filters.startDate && filters.endDate) {
          startDate = filters.startDate;
          endDate = filters.endDate;
        } else {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
    }

    try {
      const usersRef = collection(db, "users");
      let constraints = [
        where("createdAt", ">=", Timestamp.fromDate(startDate)),
        where("createdAt", "<=", Timestamp.fromDate(endDate))
      ];

      // Add language filter if specified
      if (filters.language && filters.language !== "ALL") {
        // For users, we need to check different language fields based on role
        if (filters.role === "LEARNER") {
          // Learners use activeLearningLangCode
          constraints.push(where("appMeta.activeLearningLangCode", "==", filters.language));
        } else if (filters.role === "COACH") {
          // Coaches use activeCoachingLangCode
          constraints.push(where("appMeta.activeCoachingLangCode", "==", filters.language));
        } else if (filters.role === "DUAL") {
          // For dual role users, we'll make two separate queries and combine results
          // This approach is simpler and more efficient
          const learningUsers = await this.fetchUsersWithLanguage(filters.language, "learning", startDate, endDate, filters);
          const coachingUsers = await this.fetchUsersWithLanguage(filters.language, "coaching", startDate, endDate, filters);
          
          // Combine and deduplicate users (a user might appear in both queries)
          const combinedUsers = [...learningUsers, ...coachingUsers];
          const uniqueUsers = this.removeDuplicateUsers(combinedUsers);
          
          // Group users by time period
          return this.groupUsersByTimePeriod(uniqueUsers, filters.timeGranularity, startDate, endDate);
        } else {
          // For ALL roles, check both language fields
          // We'll make two separate queries and combine results
          const learningUsers = await this.fetchUsersWithLanguage(filters.language, "learning", startDate, endDate, filters);
          const coachingUsers = await this.fetchUsersWithLanguage(filters.language, "coaching", startDate, endDate, filters);
          
          // Combine and deduplicate users (a user might appear in both queries)
          const combinedUsers = [...learningUsers, ...coachingUsers];
          const uniqueUsers = this.removeDuplicateUsers(combinedUsers);
          
          // Group users by time period
          return this.groupUsersByTimePeriod(uniqueUsers, filters.timeGranularity, startDate, endDate);
        }
      }

      // Add timezone filter if specified
      if (filters.timeZone && filters.timeZone !== "ALL") {
        if (filters.timeZone === "local") {
          // For local time, we'll handle this in the grouping logic
        } else {
          constraints.push(where("timeZone", "==", filters.timeZone));
        }
      }

      const q = query(
        usersRef,
        ...constraints,
        orderBy("createdAt", "asc")
      );

      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      // Group users by time period
      return this.groupUsersByTimePeriod(users, filters.timeGranularity, startDate, endDate);

    } catch (error) {
      console.error("Error fetching trend data:", error);
      throw new Error("Failed to fetch trend data");
    }
  }

  private async fetchSessionsByTimePeriod(filters: TrendDataFilters): Promise<TrendDataPoint[]> {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    // Calculate date ranges based on granularity
    switch (filters.timeGranularity) {
      case "daily":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case "weekly":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 83);
        break;
      case "custom":
        // Use provided startDate and endDate, or default to last 30 days
        if (filters.startDate && filters.endDate) {
          startDate = filters.startDate;
          endDate = filters.endDate;
        } else {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
    }

    try {
      const sessionsRef = collection(db, "sessions");
      const dateField = TrendDataService.DATE_FIELD_BY_COLLECTION.sessions as string;
      let constraints: any[] = [
        where(dateField as any, ">=", Timestamp.fromDate(startDate)),
        where(dateField as any, "<=", Timestamp.fromDate(endDate))
      ];

      // Add language filter if specified
      if (filters.language && filters.language !== "ALL") {
        // For sessions, we need to check different language fields based on role
        if (filters.role === "LEARNER") {
          // For learner sessions, check the session langCode or learner's language preference
          constraints.push(where("langCode", "==", filters.language));
        } else if (filters.role === "COACH") {
          // For coach sessions, check the session langCode or coach's language preference
          constraints.push(where("langCode", "==", filters.language));
        } else if (filters.role === "DUAL") {
          // For dual role sessions, check the session langCode
          constraints.push(where("langCode", "==", filters.language));
        } else {
          // For ALL roles, check the session langCode
          constraints.push(where("langCode", "==", filters.language));
        }
      }

      // Add session state filter if specified
      if (filters.sessionState && filters.sessionState !== "ALL") {
        constraints.push(where("state", "==", filters.sessionState));
      }

      // Add session type filter if specified
      if (filters.sessionType && filters.sessionType !== "ALL") {
        constraints.push(where("type", "==", filters.sessionType));
      }

      // Add coach type filter if specified
      if (filters.coachType && filters.coachType !== "ALL") {
        constraints.push(where("coachType", "==", filters.coachType));
      }

      const q = query(
        sessionsRef,
        ...constraints,
        orderBy(dateField as any, "asc")
      );

      const querySnapshot = await getDocs(q);
      const sessions = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const sessionDate: Date =
          data[dateField]?.toDate?.() ??
          data.createdAt?.toDate?.() ??
          data.startTime?.toDate?.() ??
          data.created_at?.toDate?.() ??
          new Date();
        
        return {
          id: doc.id,
          ...data,
          createdAt: sessionDate
        };
      });

      // Group sessions by time period
      return this.groupSessionsByTimePeriod(sessions, filters.timeGranularity, startDate, endDate);

    } catch (error) {
      console.error("Error fetching session trend data:", error);
      // Return empty data instead of throwing error
      return this.createEmptyTimePeriodData(filters.timeGranularity);
    }
  }

  private async fetchBookingsByTimePeriod(filters: TrendDataFilters): Promise<TrendDataPoint[]> {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    // Calculate date ranges based on granularity
    switch (filters.timeGranularity) {
      case "daily":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case "weekly":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 83);
        break;
      case "custom":
        // Use provided startDate and endDate, or default to last 30 days
        if (filters.startDate && filters.endDate) {
          startDate = filters.startDate;
          endDate = filters.endDate;
        } else {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
    }

    try {
      const bookingsRef = collection(db, "bookings");
      const dateField = TrendDataService.DATE_FIELD_BY_COLLECTION.bookings as string;
      let constraints: any[] = [
        where(dateField as any, ">=", Timestamp.fromDate(startDate)),
        where(dateField as any, "<=", Timestamp.fromDate(endDate))
      ];

      // Add language filter if specified
      if (filters.language && filters.language !== "ALL") {
        // For bookings, check the booking's langCode field
        constraints.push(where("langCode", "==", filters.language));
      }

      // Add booking status filter if specified
      if (filters.bookingStatus && filters.bookingStatus !== "ALL") {
        constraints.push(where("bookingStatus", "==", filters.bookingStatus));
      }

      // Add coach type filter if specified
      if (filters.coachType && filters.coachType !== "ALL") {
        constraints.push(where("coachType", "==", filters.coachType));
      }

      const q = query(
        bookingsRef,
        ...constraints,
        orderBy(dateField as any, "asc")
      );

      const querySnapshot = await getDocs(q);
      const bookings = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const bookingDate: Date =
          data[dateField]?.toDate?.() ??
          data.createdAt?.toDate?.() ??
          data.startTime?.toDate?.() ??
          data.created_at?.toDate?.() ??
          new Date();
        
        return {
          id: doc.id,
          ...data,
          createdAt: bookingDate
        };
      });

      // Group bookings by time period
      return this.groupBookingsByTimePeriod(bookings, filters.timeGranularity, startDate, endDate);

    } catch (error) {
      console.error("Error fetching booking trend data:", error);
      // Return empty data instead of throwing error
      return this.createEmptyTimePeriodData(filters.timeGranularity);
    }
  }

  private async fetchLoungeByTimePeriod(filters: TrendDataFilters): Promise<TrendDataPoint[]> {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    // Calculate date ranges based on granularity
    switch (filters.timeGranularity) {
      case "daily":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case "weekly":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 83);
        break;
      case "custom":
        // Use provided startDate and endDate, or default to last 30 days
        if (filters.startDate && filters.endDate) {
          startDate = filters.startDate;
          endDate = filters.endDate;
        } else {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
    }

    try {
      const loungeRef = collection(db, "lounge_requests");
      const dateField = TrendDataService.DATE_FIELD_BY_COLLECTION.lounge_requests as string;
      let constraints: any[] = [
        where(dateField as any, ">=", Timestamp.fromDate(startDate)),
        where(dateField as any, "<=", Timestamp.fromDate(endDate))
      ];

      // Add language filter if specified
      if (filters.language && filters.language !== "ALL") {
        // For lounge requests, check the langCode field
        constraints.push(where("langCode", "==", filters.language));
      }

      // Add lounge status filter if specified
      if (filters.loungeStatus && filters.loungeStatus !== "ALL") {
        constraints.push(where("status", "==", filters.loungeStatus));
      }

      // Add initiator type filter if specified
      if (filters.initiatorType && filters.initiatorType !== "ALL") {
        constraints.push(where("initiatorType", "==", filters.initiatorType));
      }

      const q = query(
        loungeRef,
        ...constraints,
        orderBy(dateField as any, "asc")
      );

      const querySnapshot = await getDocs(q);
      const loungeRequests = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const requestDate: Date =
          data[dateField]?.toDate?.() ??
          data.createdAt?.toDate?.() ??
          data.created_at?.toDate?.() ??
          new Date();
        
        return {
          id: doc.id,
          ...data,
          createdAt: requestDate
        };
      });

      // Group lounge requests by time period
      return this.groupLoungeByTimePeriod(loungeRequests, filters.timeGranularity, startDate, endDate);

    } catch (error) {
      console.error("Error fetching lounge trend data:", error);
      // Return empty data instead of throwing error
      return this.createEmptyTimePeriodData(filters.timeGranularity);
    }
  }

  private async fetchSlotsByTimePeriod(filters: TrendDataFilters): Promise<TrendDataPoint[]> {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    // Calculate date ranges based on granularity
    switch (filters.timeGranularity) {
      case "daily":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case "weekly":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 83);
        break;
      case "custom":
        // Use provided startDate and endDate, or default to last 30 days
        if (filters.startDate && filters.endDate) {
          startDate = filters.startDate;
          endDate = filters.endDate;
        } else {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
    }

    try {
      const slotsRef = collection(db, "slots");
      const dateField = TrendDataService.DATE_FIELD_BY_COLLECTION.slots as string;
      let constraints: any[] = [
        where(dateField as any, ">=", Timestamp.fromDate(startDate)),
        where(dateField as any, "<=", Timestamp.fromDate(endDate))
      ];

      // Add language filter if specified
      if (filters.language && filters.language !== "ALL") {
        // For slots, check the langCode field
        constraints.push(where("langCode", "==", filters.language));
      }

      // Add slot status filter if specified
      if (filters.slotStatus && filters.slotStatus !== "ALL") {
        constraints.push(where("status", "==", filters.slotStatus));
      }

      // Add coach type filter if specified
      if (filters.coachType && filters.coachType !== "ALL") {
        constraints.push(where("coachType", "==", filters.coachType));
      }

      const q = query(
        slotsRef,
        ...constraints,
        orderBy(dateField as any, "asc")
      );

      const querySnapshot = await getDocs(q);
      const slots = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const slotDate: Date =
          data[dateField]?.toDate?.() ??
          data.createdAt?.toDate?.() ??
          data.created_at?.toDate?.() ??
          new Date();
        
        return {
          id: doc.id,
          ...data,
          createdAt: slotDate
        };
      });

      // Group slots by time period
      return this.groupSlotsByTimePeriod(slots, filters.timeGranularity, startDate, endDate);

    } catch (error) {
      console.error("Error fetching slots trend data:", error);
      // Return empty data instead of throwing error
      return this.createEmptyTimePeriodData(filters.timeGranularity);
    }
  }

  private async fetchRoomsByTimePeriod(filters: TrendDataFilters): Promise<TrendDataPoint[]> {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    switch (filters.timeGranularity) {
      case "daily":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case "weekly":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 83);
        break;
      case "custom":
        if (filters.startDate && filters.endDate) {
          startDate = filters.startDate;
          endDate = filters.endDate;
        } else {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
    }

    try {
      const roomsRef = collection(db, "rooms");
      const dateField = TrendDataService.DATE_FIELD_BY_COLLECTION.rooms as string;
      let constraints: any[] = [
        where(dateField as any, ">=", Timestamp.fromDate(startDate)),
        where(dateField as any, "<=", Timestamp.fromDate(endDate))
      ];

      if (filters.language && filters.language !== "ALL") {
        constraints.push(where("langCode", "==", filters.language));
      }

      if (filters.roomStatus && filters.roomStatus !== "ALL") {
        constraints.push(where("status", "==", filters.roomStatus));
      }

      if (filters.roomCategory && filters.roomCategory !== "ALL") {
        constraints.push(where("category", "==", filters.roomCategory));
      }

      const q = query(
        roomsRef,
        ...constraints,
        orderBy(dateField as any, "asc")
      );

      const querySnapshot = await getDocs(q);
      const rooms = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const roomDate: Date =
          data[dateField]?.toDate?.() ??
          data.startTime?.toDate?.() ??
          data.createdAt?.toDate?.() ??
          data.created_at?.toDate?.() ??
          new Date();

        return {
          id: doc.id,
          ...data,
          createdAt: roomDate
        };
      });

      return this.groupSessionsByTimePeriod(rooms, filters.timeGranularity, startDate, endDate);
    } catch (error) {
      console.error("Error fetching room trend data:", error);
      return this.createEmptyTimePeriodData(filters.timeGranularity);
    }
  }

  private async fetchAISessionsByTimePeriod(filters: TrendDataFilters): Promise<TrendDataPoint[]> {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    switch (filters.timeGranularity) {
      case "daily":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case "weekly":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 83);
        break;
      case "custom":
        if (filters.startDate && filters.endDate) {
          startDate = filters.startDate;
          endDate = filters.endDate;
        } else {
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
        }
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
    }

    try {
      const aiSessionsRef = collection(db, "ai_sessions");
      const dateField = TrendDataService.DATE_FIELD_BY_COLLECTION.ai_sessions as string;
      let constraints: any[] = [
        where(dateField as any, ">=", Timestamp.fromDate(startDate)),
        where(dateField as any, "<=", Timestamp.fromDate(endDate))
      ];
      let languageSessionIds: Set<string> | null = null;

      if (filters.language && filters.language !== "ALL") {
        const topicsRef = collection(db, "ai_sessions_topics");
        const topicsQuery = query(topicsRef, where("language", "==", filters.language));
        const topicsSnapshot = await getDocs(topicsQuery);
        languageSessionIds = new Set(
          topicsSnapshot.docs
            .map((doc) => doc.data()?.sessionId)
            .filter((sessionId): sessionId is string => typeof sessionId === "string" && sessionId.length > 0)
        );

        if (languageSessionIds.size === 0) {
          return this.createEmptyTimePeriodData(filters.timeGranularity);
        }
      }

      if (filters.aiSessionStatus && filters.aiSessionStatus !== "ALL") {
        constraints.push(where("status", "==", filters.aiSessionStatus));
      }

      const q = query(
        aiSessionsRef,
        ...constraints,
        orderBy(dateField as any, "asc")
      );

      const querySnapshot = await getDocs(q);
      const aiSessions = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const sessionDate: Date =
          data[dateField]?.toDate?.() ??
          data.startTime?.toDate?.() ??
          data.createdAt?.toDate?.() ??
          data.created_at?.toDate?.() ??
          new Date();

        return {
          id: doc.id,
          ...data,
          createdAt: sessionDate
        };
      });

      const languageIds = languageSessionIds;
      const filteredAISessions = languageIds
        ? aiSessions.filter((session) => languageIds.has(session.id))
        : aiSessions;

      return this.groupSessionsByTimePeriod(filteredAISessions, filters.timeGranularity, startDate, endDate);
    } catch (error) {
      console.error("Error fetching AI session trend data:", error);
      return this.createEmptyTimePeriodData(filters.timeGranularity);
    }
  }

  private groupUsersByTimePeriod(
    users: any[], 
    granularity: string, 
    startDate: Date, 
    endDate: Date
  ): TrendDataPoint[] {
    const dataPoints: TrendDataPoint[] = [];
    const now = new Date();

    switch (granularity) {
      case "daily":
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          const dayUsers = users.filter(user => {
            const userDate = new Date(user.createdAt);
            return userDate.toDateString() === date.toDateString();
          });

          const learners = dayUsers.filter(user => 
            user.roles?.includes("LEARNER") && !user.roles?.includes("COACH")
          ).length;
          const coaches = dayUsers.filter(user => 
            user.roles?.includes("COACH") && !user.roles?.includes("LEARNER")
          ).length;
          const dual = dayUsers.filter(user => 
            user.roles?.includes("COACH") && user.roles?.includes("LEARNER")
          ).length;

          dataPoints.push({
            date: dateStr,
            learners,
            coaches,
            dual,
            total: learners + coaches + dual
          });
        }
        break;

      case "weekly":
        for (let i = 11; i >= 0; i--) {
          const weekStart = new Date(now);
          weekStart.setDate(weekStart.getDate() - (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          
          const weekUsers = users.filter(user => {
            const userDate = new Date(user.createdAt);
            return userDate >= weekStart && userDate <= weekEnd;
          });

          const learners = weekUsers.filter(user => 
            user.roles?.includes("LEARNER") && !user.roles?.includes("COACH")
          ).length;
          const coaches = weekUsers.filter(user => 
            user.roles?.includes("COACH") && !user.roles?.includes("LEARNER")
          ).length;
          const dual = weekUsers.filter(user => 
            user.roles?.includes("COACH") && user.roles?.includes("LEARNER")
          ).length;

          dataPoints.push({
            date: `Week ${12 - i}`,
            learners,
            coaches,
            dual,
            total: learners + coaches + dual
          });
        }
        break;

      case "custom":
        // For custom date range, create data points based on the actual date range
        if (startDate && endDate) {
          const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysDiff <= 30) {
            // For ranges up to 30 days, show daily data
            for (let i = 0; i < daysDiff; i++) {
              const date = new Date(startDate);
              date.setDate(date.getDate() + i);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              
              const dayUsers = users.filter(user => {
                const userDate = new Date(user.createdAt);
                return userDate.toDateString() === date.toDateString();
              });

              const learners = dayUsers.filter(user => 
                user.roles?.includes("LEARNER") && !user.roles?.includes("COACH")
              ).length;
              const coaches = dayUsers.filter(user => 
                user.roles?.includes("COACH") && !user.roles?.includes("LEARNER")
              ).length;
              const dual = dayUsers.filter(user => 
                user.roles?.includes("COACH") && user.roles?.includes("LEARNER")
              ).length;

              dataPoints.push({
                date: dateStr,
                learners,
                coaches,
                dual,
                total: learners + coaches + dual
              });
            }
          } else if (daysDiff <= 365) {
            // For ranges over 30 days but under 1 year, show monthly data
            const monthsDiff = Math.ceil(daysDiff / 30);
            for (let i = 0; i < monthsDiff; i++) {
              const date = new Date(startDate);
              date.setMonth(date.getMonth() + i);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              
              const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
              const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
              
              const monthUsers = users.filter(user => {
                const userDate = new Date(user.createdAt);
                return userDate >= monthStart && userDate <= monthEnd;
              });

              const learners = monthUsers.filter(user => 
                user.roles?.includes("LEARNER") && !user.roles?.includes("COACH")
              ).length;
              const coaches = monthUsers.filter(user => 
                user.roles?.includes("COACH") && !user.roles?.includes("LEARNER")
              ).length;
              const dual = monthUsers.filter(user => 
                user.roles?.includes("COACH") && user.roles?.includes("LEARNER")
              ).length;

              dataPoints.push({
                date: dateStr,
                learners,
                coaches,
                dual,
                total: learners + coaches + dual
              });
            }
          } else {
            // For ranges over 1 year, show yearly data
            const yearsDiff = Math.ceil(daysDiff / 365);
            for (let i = 0; i < yearsDiff; i++) {
              const date = new Date(startDate);
              date.setFullYear(date.getFullYear() + i);
              const dateStr = date.getFullYear().toString();
              
              const yearStart = new Date(date.getFullYear(), 0, 1);
              const yearEnd = new Date(date.getFullYear(), 11, 31);
              
              const yearUsers = users.filter(user => {
                const userDate = new Date(user.createdAt);
                return userDate >= yearStart && userDate <= yearEnd;
              });

              const learners = yearUsers.filter(user => 
                user.roles?.includes("LEARNER") && !user.roles?.includes("COACH")
              ).length;
              const coaches = yearUsers.filter(user => 
                user.roles?.includes("COACH") && !user.roles?.includes("LEARNER")
              ).length;
              const dual = yearUsers.filter(user => 
                user.roles?.includes("COACH") && user.roles?.includes("LEARNER")
              ).length;

              dataPoints.push({
                date: dateStr,
                learners,
                coaches,
                dual,
                total: learners + coaches + dual
              });
            }
          }
        }
        break;
    }

    return dataPoints;
  }

  private groupSessionsByTimePeriod(
    sessions: any[], 
    granularity: string, 
    startDate: Date, 
    endDate: Date
  ): TrendDataPoint[] {
    const now = new Date();
    const dayMs = 1000 * 60 * 60 * 24;
    const toDayStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const safeSessionDate = (session: any) => toDayStart(new Date(session.createdAt));

    if (granularity === "daily") {
      const today = toDayStart(now);
      const counts = Array(30).fill(0);

      sessions.forEach((session) => {
        const sessionDate = safeSessionDate(session);
        const diffDays = Math.floor((today.getTime() - sessionDate.getTime()) / dayMs);
        if (diffDays >= 0 && diffDays < 30) {
          counts[29 - diffDays] += 1;
        }
      });

      return counts.map((count, idx) => {
        const d = new Date(today);
        d.setDate(d.getDate() - (29 - idx));
        return {
          date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          learners: 0,
          coaches: 0,
          dual: 0,
          total: count
        };
      });
    }

    if (granularity === "weekly") {
      const today = toDayStart(now);
      const counts = Array(12).fill(0);

      sessions.forEach((session) => {
        const sessionDate = safeSessionDate(session);
        const diffDays = Math.floor((today.getTime() - sessionDate.getTime()) / dayMs);
        if (diffDays < 0 || diffDays > 77) return;

        const i = diffDays === 0 ? 0 : Math.ceil(diffDays / 7);
        if (i < 0 || i > 11) return;

        const bucketIdx = 11 - i;
        counts[bucketIdx] += 1;
      });

      return counts.map((count, idx) => ({
        date: `Week ${idx + 1}`,
        learners: 0,
        coaches: 0,
        dual: 0,
        total: count
      }));
    }

    if (granularity === "custom" && startDate && endDate) {
      const start = toDayStart(startDate);
      const end = toDayStart(endDate);
      const daysDiff = Math.ceil((end.getTime() - start.getTime()) / dayMs);

      if (daysDiff <= 30) {
        const totalDays = Math.max(1, daysDiff);
        const counts = Array(totalDays).fill(0);

        sessions.forEach((session) => {
          const sessionDate = safeSessionDate(session);
          const diffDays = Math.floor((sessionDate.getTime() - start.getTime()) / dayMs);
          if (diffDays >= 0 && diffDays < totalDays) {
            counts[diffDays] += 1;
          }
        });

        return counts.map((count, idx) => {
          const d = new Date(start);
          d.setDate(d.getDate() + idx);
          return {
            date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
            learners: 0,
            coaches: 0,
            dual: 0,
            total: count
          };
        });
      }

      if (daysDiff <= 365) {
        const monthsDiff = Math.ceil(daysDiff / 30);
        const buckets = Array.from({ length: monthsDiff }, (_, i) => {
          const d = new Date(start);
          d.setMonth(d.getMonth() + i);
          return {
            key: `${d.getFullYear()}-${d.getMonth()}`,
            label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
          };
        });
        const countMap = new Map<string, number>();
        buckets.forEach((b) => countMap.set(b.key, 0));

        sessions.forEach((session) => {
          const sessionDate = safeSessionDate(session);
          const key = `${sessionDate.getFullYear()}-${sessionDate.getMonth()}`;
          if (countMap.has(key)) {
            countMap.set(key, (countMap.get(key) ?? 0) + 1);
          }
        });

        return buckets.map((bucket) => ({
          date: bucket.label,
          learners: 0,
          coaches: 0,
          dual: 0,
          total: countMap.get(bucket.key) ?? 0
        }));
      }

      const yearsDiff = Math.ceil(daysDiff / 365);
      const years = Array.from({ length: yearsDiff }, (_, i) => start.getFullYear() + i);
      const countMap = new Map<number, number>();
      years.forEach((y) => countMap.set(y, 0));

      sessions.forEach((session) => {
        const y = safeSessionDate(session).getFullYear();
        if (countMap.has(y)) {
          countMap.set(y, (countMap.get(y) ?? 0) + 1);
        }
      });

      return years.map((year) => ({
        date: year.toString(),
        learners: 0,
        coaches: 0,
        dual: 0,
        total: countMap.get(year) ?? 0
      }));
    }

    return [];
  }

  private groupLoungeByTimePeriod(
    loungeRequests: any[], 
    granularity: string, 
    startDate: Date, 
    endDate: Date
  ): TrendDataPoint[] {
    const dataPoints: TrendDataPoint[] = [];
    const now = new Date();

    switch (granularity) {
      case "daily":
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          const dayRequests = loungeRequests.filter(request => {
            const requestDate = new Date(request.createdAt);
            return requestDate.toDateString() === date.toDateString();
          });

          // For lounge requests, we count by type or status
          const total = dayRequests.length;
          dataPoints.push({
            date: dateStr,
            learners: 0, // Lounge requests don't have learners/coaches, just total count
            coaches: 0,
            dual: 0,
            total
          });
        }
        break;

      case "weekly":
        for (let i = 11; i >= 0; i--) {
          const weekStart = new Date(now);
          weekStart.setDate(weekStart.getDate() - (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          
          const weekRequests = loungeRequests.filter(request => {
            const requestDate = new Date(request.createdAt);
            return requestDate >= weekStart && requestDate <= weekEnd;
          });

          dataPoints.push({
            date: `Week ${12 - i}`,
            learners: 0,
            coaches: 0,
            dual: 0,
            total: weekRequests.length
          });
        }
        break;

      case "custom":
        // For custom date range, create data points based on the actual date range
        if (startDate && endDate) {
          const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysDiff <= 30) {
            // For ranges up to 30 days, show daily data
            for (let i = 0; i < daysDiff; i++) {
              const date = new Date(startDate);
              date.setDate(date.getDate() + i);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              
              const dayRequests = loungeRequests.filter(request => {
                const requestDate = new Date(request.createdAt);
                return requestDate.toDateString() === date.toDateString();
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: dayRequests.length
              });
            }
          } else if (daysDiff <= 365) {
            // For ranges over 30 days but under 1 year, show monthly data
            const monthsDiff = Math.ceil(daysDiff / 30);
            for (let i = 0; i < monthsDiff; i++) {
              const date = new Date(startDate);
              date.setMonth(date.getMonth() + i);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              
              const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
              const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
              
              const monthRequests = loungeRequests.filter(request => {
                const requestDate = new Date(request.createdAt);
                return requestDate >= monthStart && requestDate <= monthEnd;
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: monthRequests.length
              });
            }
          } else {
            // For ranges over 1 year, show yearly data
            const yearsDiff = Math.ceil(daysDiff / 365);
            for (let i = 0; i < yearsDiff; i++) {
              const date = new Date(startDate);
              date.setFullYear(date.getFullYear() + i);
              const dateStr = date.getFullYear().toString();
              
              const yearStart = new Date(date.getFullYear(), 0, 1);
              const yearEnd = new Date(date.getFullYear(), 11, 31);
              
              const yearRequests = loungeRequests.filter(request => {
                const requestDate = new Date(request.createdAt);
                return requestDate >= yearStart && requestDate <= yearEnd;
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: yearRequests.length
              });
            }
          }
        }
        break;
    }

    return dataPoints;
  }

  private groupSlotsByTimePeriod(
    slots: any[], 
    granularity: string, 
    startDate: Date, 
    endDate: Date
  ): TrendDataPoint[] {
    const dataPoints: TrendDataPoint[] = [];
    const now = new Date();

    switch (granularity) {
      case "daily":
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          const daySlots = slots.filter(slot => {
            const slotDate = new Date(slot.createdAt);
            return slotDate.toDateString() === date.toDateString();
          });

          // For slots, we count by type or status
          const total = daySlots.length;
          dataPoints.push({
            date: dateStr,
            learners: 0, // Slots don't have learners/coaches, just total count
            coaches: 0,
            dual: 0,
            total
          });
        }
        break;

      case "weekly":
        for (let i = 11; i >= 0; i--) {
          const weekStart = new Date(now);
          weekStart.setDate(weekStart.getDate() - (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          
          const weekSlots = slots.filter(slot => {
            const slotDate = new Date(slot.createdAt);
            return slotDate >= weekStart && slotDate <= weekEnd;
          });

          dataPoints.push({
            date: `Week ${12 - i}`,
            learners: 0,
            coaches: 0,
            dual: 0,
            total: weekSlots.length
          });
        }
        break;

      case "custom":
        // For custom date range, create data points based on the actual date range
        if (startDate && endDate) {
          const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysDiff <= 30) {
            // For ranges up to 30 days, show daily data
            for (let i = 0; i < daysDiff; i++) {
              const date = new Date(startDate);
              date.setDate(date.getDate() + i);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              
              const daySlots = slots.filter(slot => {
                const slotDate = new Date(slot.createdAt);
                return slotDate.toDateString() === date.toDateString();
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: daySlots.length
              });
            }
          } else if (daysDiff <= 365) {
            // For ranges over 30 days but under 1 year, show monthly data
            const monthsDiff = Math.ceil(daysDiff / 30);
            for (let i = 0; i < monthsDiff; i++) {
              const date = new Date(startDate);
              date.setMonth(date.getMonth() + i);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
              const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
              
              const monthSlots = slots.filter(slot => {
                const slotDate = new Date(slot.createdAt);
                return slotDate >= monthStart && slotDate <= monthEnd;
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: monthSlots.length
              });
            }
          } else {
            // For ranges over 1 year, show yearly data
            const yearsDiff = Math.ceil(daysDiff / 365);
            for (let i = 0; i < yearsDiff; i++) {
              const date = new Date(startDate);
              date.setFullYear(date.getFullYear() + i);
              const dateStr = date.getFullYear().toString();
              
              const yearStart = new Date(date.getFullYear(), 0, 1);
              const yearEnd = new Date(date.getFullYear(), 11, 31);
              
              const yearSlots = slots.filter(slot => {
                const slotDate = new Date(slot.createdAt);
                return slotDate >= yearStart && slotDate <= yearEnd;
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: yearSlots.length
              });
            }
          }
        }
        break;
    }

    return dataPoints;
  }

  private groupBookingsByTimePeriod(
    bookings: any[], 
    granularity: string, 
    startDate: Date, 
    endDate: Date
  ): TrendDataPoint[] {
    const dataPoints: TrendDataPoint[] = [];
    const now = new Date();

    switch (granularity) {
      case "daily":
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          const dayBookings = bookings.filter(booking => {
            const bookingDate = new Date(booking.createdAt);
            return bookingDate.toDateString() === date.toDateString();
          });

          // For bookings, we count by type or status
          const total = dayBookings.length;
          dataPoints.push({
            date: dateStr,
            learners: 0, // Bookings don't have learners/coaches, just total count
            coaches: 0,
            dual: 0,
            total
          });
        }
        break;

      case "weekly":
        for (let i = 11; i >= 0; i--) {
          const weekStart = new Date(now);
          weekStart.setDate(weekStart.getDate() - (i * 7));
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 6);
          
          const weekBookings = bookings.filter(booking => {
            const bookingDate = new Date(booking.createdAt);
            return bookingDate >= weekStart && bookingDate <= weekEnd;
          });

          dataPoints.push({
            date: `Week ${12 - i}`,
            learners: 0,
            coaches: 0,
            dual: 0,
            total: weekBookings.length
          });
        }
        break;

      case "custom":
        // For custom date range, create data points based on the actual date range
        if (startDate && endDate) {
          const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (daysDiff <= 30) {
            // For ranges up to 30 days, show daily data
            for (let i = 0; i < daysDiff; i++) {
              const date = new Date(startDate);
              date.setDate(date.getDate() + i);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              
              const dayBookings = bookings.filter(booking => {
                const bookingDate = new Date(booking.createdAt);
                return bookingDate.toDateString() === date.toDateString();
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: dayBookings.length
              });
            }
          } else if (daysDiff <= 365) {
            // For ranges over 30 days but under 1 year, show monthly data
            const monthsDiff = Math.ceil(daysDiff / 30);
            for (let i = 0; i < monthsDiff; i++) {
              const date = new Date(startDate);
              date.setMonth(date.getMonth() + i);
              const dateStr = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
              
              const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
              const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
              
              const monthBookings = bookings.filter(booking => {
                const bookingDate = new Date(booking.createdAt);
                return bookingDate >= monthStart && bookingDate <= monthEnd;
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: monthBookings.length
              });
            }
          } else {
            // For ranges over 1 year, show yearly data
            const yearsDiff = Math.ceil(daysDiff / 365);
            for (let i = 0; i < yearsDiff; i++) {
              const date = new Date(startDate);
              date.setFullYear(date.getFullYear() + i);
              const dateStr = date.getFullYear().toString();
              
              const yearStart = new Date(date.getFullYear(), 0, 1);
              const yearEnd = new Date(date.getFullYear(), 11, 31);
              
              const yearBookings = bookings.filter(booking => {
                const bookingDate = new Date(booking.createdAt);
                return bookingDate >= yearStart && bookingDate <= yearEnd;
              });

              dataPoints.push({
                date: dateStr,
                learners: 0,
                coaches: 0,
                dual: 0,
                total: yearBookings.length
              });
            }
          }
        }
        break;
    }

    return dataPoints;
  }

  private createEmptyTimePeriodData(granularity: string): TrendDataPoint[] {
    const dataPoints: TrendDataPoint[] = [];
    const now = new Date();

    switch (granularity) {
      case "daily":
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i);
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          
          dataPoints.push({
            date: dateStr,
            learners: 0,
            coaches: 0,
            dual: 0,
            total: 0
          });
        }
        break;

      case "weekly":
        for (let i = 11; i >= 0; i--) {
          dataPoints.push({
            date: `Week ${12 - i}`,
            learners: 0,
            coaches: 0,
            dual: 0,
            total: 0
          });
        }
        break;

      case "custom":
        // For custom date range, create empty data points
        dataPoints.push({
          date: "Custom Range",
          learners: 0,
          coaches: 0,
          dual: 0,
          total: 0
        });
        break;
    }

    return dataPoints;
  }

  public async fetchTrendData(filters: TrendDataFilters): Promise<TrendDataResponse> {
    try {
      let data: TrendDataPoint[] = [];

      switch (filters.metric) {
        case "users":
          data = await this.fetchUsersByTimePeriod(filters);
          break;
        case "sessions":
          data = await this.fetchSessionsByTimePeriod(filters);
          break;
        case "bookings":
          data = await this.fetchBookingsByTimePeriod(filters);
          break;
        case "lounge":
          data = await this.fetchLoungeByTimePeriod(filters);
          break;
        case "slots":
          data = await this.fetchSlotsByTimePeriod(filters);
          break;
        case "rooms":
          data = await this.fetchRoomsByTimePeriod(filters);
          break;
        case "ai-sessions":
          data = await this.fetchAISessionsByTimePeriod(filters);
          break;
        default:
          data = await this.fetchUsersByTimePeriod(filters);
      }

      const now = new Date();
      let startDate: Date;
      
      switch (filters.timeGranularity) {
        case "daily":
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
          break;
        case "weekly":
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 83);
          break;
        case "custom":
          // Use provided startDate and endDate, or default to last 30 days
          if (filters.startDate && filters.endDate) {
            startDate = filters.startDate;
          } else {
            startDate = new Date(now);
            startDate.setDate(startDate.getDate() - 29);
          }
          break;
        default:
          startDate = new Date(now);
          startDate.setDate(startDate.getDate() - 29);
      }

      return {
        data,
        totalPoints: data.length,
        timeRange: {
          start: startDate.toISOString(),
          end: now.toISOString()
        }
      };

    } catch (error) {
      console.error("Error in fetchTrendData:", error);
      throw error;
    }
  }

  // Helper method to fetch users with a specific language preference
  private async fetchUsersWithLanguage(
    language: string, 
    type: "learning" | "coaching", 
    startDate: Date, 
    endDate: Date, 
    filters: TrendDataFilters
  ): Promise<any[]> {
    try {
      const usersRef = collection(db, "users");
      const field = type === "learning" ? "appMeta.activeLearningLangCode" : "appMeta.activeCoachingLangCode";
      
      const constraints = [
        where("createdAt", ">=", Timestamp.fromDate(startDate)),
        where("createdAt", "<=", Timestamp.fromDate(endDate)),
        where(field, "==", language)
      ];

      // Add timezone filter if specified
      if (filters.timeZone && filters.timeZone !== "ALL") {
        if (filters.timeZone === "local") {
          // For local time, we'll handle this in the grouping logic
        } else {
          constraints.push(where("timeZone", "==", filters.timeZone));
        }
      }

      const q = query(
        usersRef,
        ...constraints,
        orderBy("createdAt", "asc")
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
    } catch (error) {
      console.error(`Error fetching ${type} users with language ${language}:`, error);
      return [];
    }
  }

  // Helper method to remove duplicate users based on ID
  private removeDuplicateUsers(users: any[]): any[] {
    const seen = new Set();
    return users.filter(user => {
      if (seen.has(user.id)) {
        return false;
      }
      seen.add(user.id);
      return true;
    });
  }
}

export const trendDataService = new TrendDataService();
